import React from "react";
import "./CoreCapabilities.css";

type IconName = "chip" | "shield" | "physical" | "dft" | "layers";

type ServiceItem = {
  title: string;
  description: string;
  icon: IconName;
};

const coreServices: ServiceItem[] = [
  {
    title: "ASIC & SoC Design",
    description:
      "RTL design, SoC design, IP integration, architecture support and digital design engineering.",
    icon: "chip",
  },
  {
    title: "Verification Engineering",
    description:
      "Functional verification, UVM, assertion-based verification, emulation and verification methodologies.",
    icon: "shield",
  },
  {
    title: "Physical Design",
    description:
      "Floorplanning, place & route, clock tree synthesis, timing closure, physical verification and sign-off.",
    icon: "physical",
  },
  {
    title: "Design for Test (DFT)",
    description:
      "Scan insertion, ATPG, MBIST, boundary scan and test architecture implementation.",
    icon: "dft",
  },
  {
    title: "Silicon Realization",
    description:
      "Engineering support across implementation, validation and successful silicon realization.",
    icon: "layers",
  },
];

const specializedCapabilities = [
  "Analog & Mixed Signal Design",
  "Static Timing Analysis",
  "Embedded Software Systems",
  "FPGA Engineering",
  "Silicon Validation",
  "EDA & Design Automation",
  "Hardware Platform Engineering",
  "Product Engineering",
  "Networking Solutions",
];

const CapabilityIcon: React.FC<{ name: IconName }> = ({ name }) => {
  const common = {
    width: 38,
    height: 38,
    viewBox: "0 0 48 48",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  const icons: Record<IconName, React.ReactNode> = {
    chip: (
      <>
        <rect x="12" y="12" width="24" height="24" rx="3" />
        <rect x="19" y="19" width="10" height="10" rx="1" />
        <path d="M17 4v8M24 4v8M31 4v8M17 36v8M24 36v8M31 36v8" />
        <path d="M4 17h8M4 24h8M4 31h8M36 17h8M36 24h8M36 31h8" />
      </>
    ),
    shield: (
      <>
        <path d="M24 5 39 11v11c0 10-6 17-15 21C15 39 9 32 9 22V11l15-6Z" />
        <path d="m18 24 4 4 8-9" />
      </>
    ),
    physical: (
      <>
        <rect x="7" y="7" width="14" height="14" />
        <rect x="27" y="7" width="14" height="14" />
        <rect x="7" y="27" width="14" height="14" />
        <rect x="27" y="27" width="14" height="14" />
        <path d="M21 14h6M14 21v6M34 21v6M21 34h6" />
      </>
    ),
    dft: (
      <>
        <rect x="15" y="15" width="18" height="18" rx="2" />
        <circle cx="24" cy="24" r="4" />
        <path d="M24 15v5M24 28v5M15 24h5M28 24h5" />
      </>
    ),
    layers: (
      <>
        <path d="m24 6 17 9-17 9L7 15l17-9Z" />
        <path d="m7 23 17 9 17-9" />
        <path d="m7 31 17 9 17-9" />
      </>
    ),
  };

  return <svg {...common}>{icons[name]}</svg>;
};

const CoreCapabilities: React.FC = () => {
  return (
    <section className="core-capabilities-section" id="core-capabilities">
      <div className="core-capabilities-container">
        <header className="core-capabilities-hero">
          <span className="core-capabilities-eyebrow">Core Capabilities</span>
          <h2>Engineering capabilities for complete silicon development</h2>
          <p>
            Digicomm Semiconductor delivers core semiconductor engineering services
            supported by specialised engineering capabilities and flexible engagement
            models, enabling customers to execute complex silicon programs with
            confidence. Our multidisciplinary expertise spans the complete semiconductor
            development lifecycle—from digital design through reliable silicon realization.
          </p>
        </header>

        <div className="core-capabilities-block">
          <div className="core-capabilities-section-title">
      {/*  <span>01</span> */}
            <h3>Core Semiconductor Engineering Services</h3>
          </div>

          <div className="core-capabilities-services">
            {coreServices.map((service) => (
              <article className="core-capabilities-card" key={service.title}>
                <div className="core-capabilities-card__icon">
                  <CapabilityIcon name={service.icon} />
                </div>
                <h4>{service.title}</h4>
                <p>{service.description}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="core-capabilities-block">
          <div className="core-capabilities-section-title">
               {/*   <span>02</span> */}
            <h3>Specialized Engineering Capabilities</h3>
          </div>

          <div className="core-capabilities-chips">
            {specializedCapabilities.map((capability) => (
              <span className="core-capabilities-chip" key={capability}>
                {capability}
              </span>
            ))}
          </div>
        </div>

        <div className="core-capabilities-bottom-grid">
          <article className="core-capabilities-panel">
             {/*     <span className="core-capabilities-panel__number">03</span>*/}
            <h3>Engineering Engagement Models</h3>
            <div className="core-capabilities-models">
              <div>Engineering Consulting</div>
              <div>Dedicated Engineering Teams</div>
            </div>
          </article>

          <article className="core-capabilities-panel core-capabilities-panel--methodology">
           {/* <span className="core-capabilities-panel__number">04</span> */}
            <h3>Our Delivery Methodology</h3>
            <p>
              Structured engineering execution through collaborative teams, quality
              processes and disciplined project delivery.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
};

export default CoreCapabilities;